import React, { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw } from 'lucide-react'

interface ErrorBoundaryProps {
 children: ReactNode
 fallback?: ReactNode
 onRetry?: () => void
 name?: string
}

interface ErrorBoundaryState {
 hasError: boolean
 error: Error | null
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
 constructor(props: ErrorBoundaryProps) {
 super(props)
 this.state = { hasError: false, error: null }
 }

 static getDerivedStateFromError(error: Error): ErrorBoundaryState {
 return { hasError: true, error }
 }

 componentDidCatch(error: Error, info: ErrorInfo) {
 console.error(`[ErrorBoundary${this.props.name ?`:${this.props.name}` :''}]`, error, info.componentStack)
 }

 handleRetry = () => {
 this.setState({ hasError: false, error: null })
 this.props.onRetry?.()
 }

 render() {
 if (this.state.hasError) {
 if (this.props.fallback) return this.props.fallback

 return (
 <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
 <div className="w-14 h-14 rounded-2xl bg-error-50 flex items-center justify-center mb-4">
 <AlertTriangle size={28} className="text-error-500" />
 </div>
 <h3 className="text-lg font-semibold text-gray-900 mb-1">
 {this.props.name ||'Something went wrong'}
 </h3>
 <p className="text-sm text-gray-500 mb-6 max-w-sm">
 {this.state.error?.message ||'An unexpected error occurred. Please try again.'}
 </p>
 <button
 onClick={this.handleRetry}
 className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-500 text-white text-sm font-semibold hover:bg-brand-600 transition-colors"
 >
 <RefreshCw size={16} />
 Try Again
 </button>
 {this.state.error && (
 <details className="mt-4 text-left max-w-md">
 <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-400">Error details</summary>
 <pre className="mt-2 text-[10px] text-gray-500 bg-surface-100 p-3 rounded-lg overflow-auto max-h-32">
 {this.state.error.stack}
 </pre>
 </details>
 )}
 </div>
 )
 }

 return this.props.children
 }
}